import { Injectable } from '@angular/core';
import { ILot } from '@ml/common';
import { LotVM } from '../../site-map/models/lot-vm';
import { Lookup } from '../helpers/lookup';
import { NeighborhoodVM } from '../models/neighborhood-vm';
import { ApiService } from './api.service';
import { CommunityService } from './community.service';
import { NeighborhoodService } from './neighborhood.service';

@Injectable({
  providedIn: 'root'
})
export class LotFitService {
  private neighborhood: NeighborhoodVM;
  private clonedMapSvg: SVGElement;
  private currentLotSvg: SVGElement;

  constructor(
    private api: ApiService,
    protected neighborhoodService: NeighborhoodService,
    protected communityService: CommunityService,
    protected lookup: Lookup
  ) {}

  async initLotfitOnLot(clone: SVGElement, lot: LotVM): Promise<void> {
    if (!this.neighborhood || this.neighborhood.NeighborhoodId !== lot.NeighborhoodId) {
      const commBaseUrl = this.communityService.getBaseApiUrl();
      const hood = this.neighborhoodService.get(lot.NeighborhoodId);
      if (hood) {
        this.neighborhood = new NeighborhoodVM(
          hood,
          commBaseUrl,
          this.lookup.ApiProductDataEndpoint
        );
      }
    }
    if (clone && lot) {
      this.setClonedMapSvg(clone);
      this.currentLotSvg = this.getLotSvg(lot.UIInstanceId) as SVGElement;
      if (this.currentLotSvg) {
        const hasArt = this.currentLotSvg.querySelector('[id*=-LotFit-]');
        if (!hasArt) {
          await this.getLotArtworkAndInject(this.currentLotSvg, lot, true, true);
        }
      }
    }
  }

  setClonedMapSvg(svg: SVGElement) {
    this.clonedMapSvg = svg;
  }

  clearClonedMapSvg() {
    this.clonedMapSvg = null;
  }

  reset() {
    this.clonedMapSvg = null;
    this.currentLotSvg = null;
    this.neighborhood = null;
  }

  getLotSvg(uiInstanceId: string) {
    return this.clonedMapSvg?.querySelector(`#${uiInstanceId}`);
  }

  changeVisibleFootPrint(
    lot: ILot,
    fpCuid: string,
    swing: string,
    elevCuid: string,
    lotSvg?: SVGElement
  ) {
    let effLotSvg: SVGElement;
    if (lotSvg) {
      effLotSvg = lotSvg;
    } else if (this.currentLotSvg) {
      effLotSvg = this.currentLotSvg;
    }

    if (effLotSvg) {
      const allLotFitNodes = effLotSvg.querySelectorAll('[id*="-LotFit-"]');
      if (allLotFitNodes.length) {
        const allLotFitElems = Array.from(allLotFitNodes) as HTMLElement[];
        const qString = this.getLotFitQueryString(lot, fpCuid, swing);
        const layerToActivate = [];

        if (qString) {
          let effQstring = qString;
          if (elevCuid) {
            effQstring += `-${elevCuid}`;
          }
          allLotFitElems.forEach(el => {
            if (el.id.includes(effQstring)) {
              layerToActivate.push(el);
            }
          });

          //Fall back to no elevation match
          if (!layerToActivate.length) {
            allLotFitElems.forEach(el => {
              if (el.id.includes(qString)) {
                layerToActivate.push(el);
              }
            });
          }

          //Now set visibility
          allLotFitElems.forEach(el => {
            if (layerToActivate.find(e => e.id === el.id)) {
              el.style.visibility = 'visible';
            } else {
              el.style.visibility = 'hidden';
            }
          });
        }
      }
    }
  }

  setLotFitOnLot(lotSvg: SVGElement, lot: ILot, isVisible: boolean, showDimsAndName: boolean) {
    if (this.neighborhood?.NeighborhoodId !== lot.NeighborhoodId) {
      const commBaseUrl = this.communityService.getBaseApiUrl();
      const hood = this.neighborhoodService.get(lot.NeighborhoodId);
      if (hood) {
        this.neighborhood = new NeighborhoodVM(
          hood,
          commBaseUrl,
          this.lookup.ApiProductDataEndpoint
        );
      }
    }
    const hasArt = lotSvg.querySelector('[id*=-LotFit-]');
    if (hasArt) {
      this.setLotFitVisibility(lotSvg, lot, isVisible, showDimsAndName);
    }
  }

  lotHasLotFitAvailable(lot: ILot): boolean {
    // Must have at least 1 floorplan and swing mapping to have lotfit available
    if (lot && lot.CustomData && lot.CustomData.HasSplitFile === 'true') {
      const mappings = this.getParsedSwingMappings(lot);

      if (lot.MappedFloorPlanIds?.length && mappings.size > 0) {
        return true;
      }
      return false;
    }
    return false;
  }

  getParsedSwingMappings(lot: ILot): Map<number, SwingMappings> | null {
    if (lot.CustomData?.HasSplitFile === 'true' && lot.CustomData?.FloorplanToGarageSwing) {
      const parsedSwings = JSON.parse(lot.CustomData.FloorplanToGarageSwing);
      const swingMap = new Map<number, SwingMappings>();
      Object.keys(parsedSwings).forEach(fpIdStr => {
        const fpId = Number(fpIdStr);
        if (!isNaN(fpId)) {
          swingMap.set(fpId, new SwingMappings(parsedSwings[fpIdStr]));
        }
      });
      return swingMap;
    }
    return null;
  }

  async setFromPrintBrochure(
    lot: ILot,
    fpCuid: string,
    swing: string,
    elevCuid: string,
    mapSvg: SVGElement
  ) {
    if (mapSvg) {
      const foundLotSvg = mapSvg.querySelector(`#${lot.UIInstanceId}`);
      if (foundLotSvg) {
        const lotSvg = foundLotSvg as SVGElement;
        const hasArt = lotSvg.querySelector('[id*=-LotFit-]');
        if (!hasArt) {
          await this.getLotArtworkAndInject(lotSvg, lot, true, true);
        }
        this.changeVisibleFootPrint(lot, fpCuid, swing, elevCuid, lotSvg);
      }
    }
  }

  private setLotFitVisibility(
    lotSvg: SVGElement,
    lot: ILot,
    isVisible: boolean,
    showDimsAndName: boolean
  ) {
    const lotFitElemList = lotSvg.querySelectorAll('[id*="-LotFit-"]');

    if (lotFitElemList.length) {
      const allLotFitElems = Array.from(lotFitElemList) as HTMLElement[];
      if (isVisible) {
        const finalLayers = allLotFitElems.filter(elem => elem.id.includes('-FINAL'));
        if (finalLayers.length) {
          finalLayers.forEach(elem => {
            if (showDimsAndName || elem.id.endsWith('-Art')) {
              elem.style.visibility = 'visible';
            } else {
              elem.style.visibility = 'hidden';
            }
          });
        }
      } else {
        allLotFitElems.forEach(elem => {
          elem.style.visibility = 'hidden';
        });
      }
    }
  }

  private async getLotArtworkAndInject(
    lotSvg: SVGElement,
    lot: ILot,
    isVisible: boolean,
    showDetails: boolean
  ): Promise<void> {
    if (!this.neighborhood) return;
    const svgUrl = `${this.neighborhood.BaseUrl}/LotFit/${lot.UIInstanceId}.svg`;

    const res = await this.api.getWithNoAuth(svgUrl);

    if (typeof res === 'string' && res.length) {
      const doc = new DOMParser().parseFromString(res, 'image/svg+xml');
      const svg = doc.documentElement;
      const children = Array.from(svg.children);

      // Inject into lotSvg
      const lotNumber = lotSvg.querySelector('[id*=TEXT_]');

      children.forEach(c => {
        if (lotNumber) {
          lotSvg.insertBefore(c, lotNumber);
        } else {
          lotSvg.appendChild(c);
        }
      });

      this.setLotFitVisibility(lotSvg, lot, isVisible, showDetails);
    }
  }

  private getLotFitQueryString(lot: ILot, fpCuid: string, swing: string): string {
    if (lot.UIInstanceId && fpCuid && swing) {
      const qString = `${lot.UIInstanceId}-LotFit-${swing}-${fpCuid}`;
      return qString;
    } else {
      return '';
    }
  }
}

export class SwingMappings {
  left: boolean;
  right: boolean;

  constructor(fpSwings) {
    Object.assign(this, fpSwings);
  }
}
